import jalaliMoment from "jalali-moment";
import moment from "moment";

import {
  DepartmentValue,
  OrderStatusValue,
  TicketStatusValue,
} from "./Constants";
import { yearMonths } from "./Date";

export const convertBlobToFile = (blob) => {
  const imageType = blob.type.split("/")[1];
  const imageFile = new File(
    [blob],
    `image_${generateRandomNumber()}.${imageType}`,
    { type: imageType }
  );
  return imageFile;
};

/**
 * Generate random number
 */
export const generateRandomNumber = () =>
  Math.floor(100000 + Math.random() * 900000000);

/**
 * Generate file name using random number for uploading to server
 *
 * @param {File} file
 * @returns {string}
 */
export const generateFileName = (file) => {
  return `${generateRandomNumber()}_${file.name}`;
};

export const isEnglishDigits = /^([0-9(),.!?_-\s]+)$/;

/**
 * Convert all digits in a string to english
 * @param {string} num
 * @returns {string}
 */
export const convertToEnglishDigit = (num) => {
  return num
    ?.toString()
    ?.replace(/[\u0660-\u0669]/g, (c) => (c.charCodeAt(0) - 0x0660).toString())
    ?.replace(/[\u06f0-\u06f9]/g, (c) => (c.charCodeAt(0) - 0x06f0).toString());
};

/**
 * @param {Object} order
 * @returns {Promise}
 */
export const generateReceiptNumber = async (order, providerId, apiService) => {
  if (order?.receipt_number) {
    return {
      num: order.receipt_number,
      reprint: true,
    };
  }

  const res = await apiService.generateReceiptNumber(providerId, order.id);

  return {
    num: res.data.receipt_number,
    reprint: false,
  };
};

/**
 * Checking user is on mobile or desktop
 * @returns {boolean}
 */
export const isMobileView = () => {
  let check = false;
  (function (a) {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
        a
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\\-(n|u)|c55\/|capi|ccwa|cdm\\-|cell|chtm|cldc|cmd\\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\\-s|devi|dica|dmob|do(c|p)o|ds(12|\\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\\-|_)|g1 u|g560|gene|gf\\-5|g\\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\\-(m|p|t)|hei\\-|hi(pt|ta)|hp( i|ip)|hs\\-c|ht(c(\\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\\-(20|go|ma)|i230|iac( |\\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\\-[a-w])|libw|lynx|m1\\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\\-2|po(ck|rt|se)|prox|psio|pt\\-g|qa\\-a|qc(07|12|21|32|60|\\-[2-7]|i\\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\\-|oo|p\\-)|sdk\/|se(c(\\-|0|1)|47|mc|nd|ri)|sgh\\-|shar|sie(\\-|m)|sk\\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\\-|v\\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\\-|tdg\\-|tel(i|m)|tim\\-|t\\-mo|to(pl|sh)|ts(70|m\\-|m3|m5)|tx\\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\\-|your|zeto|zte\\-/i.test(
        a.substr(0, 4)
      )
    )
      check = true;
  })(navigator.userAgent || navigator.vendor || window.opera);
  return check;
};

/**
 * Get current datetime from moment-timezone standard time (Asia/Tehran)
 * @param date
 * @returns
 */
export const getMoment = () => {
  return moment();
};

/**
 * Return proper ticket status based on given response from api.
 * @param {TicketStatusType} ticketStatus
 * @returns {string}
 */
export const getTicketStatus = (ticketStatus = "") => {
  return (
    TicketStatusValue[ticketStatus.toUpperCase()] || TicketStatusValue.OTHER
  );
};

/**
 * Return proper order status based on given response from api.
 * @param {OrderStatusType} orderStatus
 * @returns {string}
 */
export const getOrderStatus = (orderStatus = "") => {
  return OrderStatusValue[orderStatus.toUpperCase()] || OrderStatusValue.OTHER;
};

/**
 * Return proper ticket department label based on given response from api.
 * @param {DepartmentType} department
 * @returns {string}
 */
export const getDepartmentLabel = (department = "") => {
  const departmentValue =
    DepartmentValue[department.toUpperCase()] || DepartmentValue.OTHER;
  return departmentValue;
};

/**
 * Convert given date to human readable
 * @param {string} date
 * @param {boolean} dateIncluded
 * @param {boolean} timeIncluded
 * @return {string}
 */
export const convertDateToHumanReadable = (
  date,
  timeIncluded = false,
  dateIncluded = true
) => {
  let day,
    month,
    year,
    time = "";
  let fullDate,
    fullTime = "";

  if (dateIncluded) {
    day = jalaliMoment(date).format("jDD");
    month = yearMonths[jalaliMoment(date).jMonth()];
    year = jalaliMoment(date).format("jYYYY");
    fullDate = `${day} ${month} ${year}`;
  }

  if (timeIncluded) {
    time = jalaliMoment(date).format("HH:mm");
    fullTime = `${time} - `;
  }

  return fullTime + fullDate;
};

/**
 * Get mime type from attachment url by spliting dot from url
 * @param {File} attachment
 * @returns
 */
export const getMimeType = (attachment) => {
  return attachment.url ? `.${attachment.url.split(".").at(-1)}` : "";
};

/**
 * Download an image or a file
 * @param attachment
 */
export const downloadFile = (attachment) => {
  fetch(attachment.url, {
    method: "GET",
    headers: {},
  })
    .then((response) => {
      response.arrayBuffer().then(function (buffer) {
        const url = window.URL.createObjectURL(new Blob([buffer]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          attachment.name + getMimeType(attachment)
        ); // or any other extension
        document.body.appendChild(link);
        link.click();
      });
    })
    .catch((err) => {
      console.log(err);
    });
};

/**
 * Config for tracking progress event
 * @param {*} progressEvent
 * @returns
 */
export const onUploadProgress = (progressEvent) => {
  const { loaded, total } = progressEvent;
  const percent = Math.floor((loaded * 100) / total);
  return percent;
};
